// The phosphor-dark palette, adopted from dse-terminal (src/theme.css:52-114).
//
// This is the RAW palette layer. Nothing should import it directly except styles/tokens.ts —
// components import the semantic names from there, so a future theme swap is one file.
//
// PORTABILITY NOTE: eight of dse-terminal's tokens are built with CSS color-mix(), which cannot
// appear in a TS string handed to Recharts (stroke/fill props reject it). Every one of those is
// resolved here to its literal sRGB value against --panel #10151b.

export const phosphor = {
  // ── surfaces ──────────────────────────────────────────────────────────────
  page: '#0a0d10',
  panel: '#10151b',
  inset: '#0c1014',
  line: '#1c242e',
  lineStrong: '#2a3540',

  // ── ink ───────────────────────────────────────────────────────────────────
  ink: '#d7dde4',
  inkBright: '#eef2f6',
  muted: '#7e8a96',
  /**
   * The dimmest ink that still clears 4.5:1 WCAG AA on panel AND on the chrome sheen.
   * Use this for any text that carries information.
   */
  textDim: '#7a8693',
  /** DECORATIVE / DISABLED ONLY. Fails AA — never use for information-carrying text. */
  faint: '#46525e',

  // ── chrome / brand ────────────────────────────────────────────────────────
  /**
   * Amber is the CHROME accent: nav rail, panel headers, badges, focus glow. It is
   * deliberately NOT the warning colour here — see `warn` below.
   */
  amber: '#e8a33d',
  amberHi: '#ffc36b',

  // ── data semantics ────────────────────────────────────────────────────────
  blue: '#4da3ff',
  green: '#36c98e',
  red: '#ff5c5c',
  /** Toxicity / adverse selection. */
  tox: '#c77dff',
  cyan: '#3ec9d6',

  /**
   * WARNING — re-homed, and this is the one deliberate departure from phosphor.
   *
   * dse-terminal spends amber on BOTH chrome and the warn semantic, which works there because
   * it has few warning surfaces. We have ~140, plus a mode chip that must shout when the engine
   * is on simulated data. Sharing a hue would make every warning read as decoration. This hot
   * orange sits clearly apart from chrome gold #e8a33d and from sell-red #ff5c5c.
   */
  warn: '#ff8c3a',

  /** Text laid over a solid accent fill. */
  onAccent: '#0a0d10',

  // ── soft tints (color-mix resolved to literals) ───────────────────────────
  blueSoft: '#192a3d',
  greenSoft: '#16302c',
  redSoft: '#342025',
  amberSoft: '#302a20',
  toxSoft: '#2b253d',
  warnSoft: '#31251c',
} as const;

/**
 * Fonts. We keep OUR pairing — Sora for display, JetBrains Mono for figures — rather than
 * phosphor's Oxanium/IBM Plex Mono. Only the colours were adopted.
 *
 * Both are now self-hosted variable woff2 in public/fonts and @font-face'd in index.css.
 * That matters beyond taste: tokens.ts had declared JetBrains Mono while nothing ever loaded
 * it, so every figure the app rendered as "monospace" was actually the system default and
 * tabular-nums alignment never took effect.
 */
export const phosphorFonts = {
  display: '"Sora", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, Consolas, monospace',
} as const;
